import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { TrackerA } from "@/components/Tracker";
import { BuyVipDialog } from "@/components/Vip";
import useAppStore from "@/context/store";
import { goGdMerge } from "@/pageComponents/GdMerge";
import { gaodingEditUrl } from "@/pageComponents/GdMerge/shared";
import { getGrocery } from "@/services";
import { isGdMerged } from "@/utils/gaoding";
import { parseVipUrlHash } from "@/utils/gaodingVip";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import useReq from "@/utils/useReq";
import { useHover, useMemoizedFn } from "ahooks";
import { Dropdown } from "antd";
import classNames from "classnames";
import { useMemo, useRef } from "react";
import { useIsNewHeader } from "../headerContext";
import styles from "./vip.module.less";

interface IConf {
  text: string;
  color: string;
  bg: string;
  icon: GroceryConfig.ImageData;
  rightTag: GroceryConfig.ImageData;
  href?: string;
  popover: {
    // head配置
    title: string;
    desc: string | { text: string }[];
    headBg: GroceryConfig.ImageData;
    moreHref: string;
    moreColor?: string;

    //卡片hover配置
    cardHoverBgColor: string;
    cardHoverBorderColor: string;
    cardHoverBtnBg: string;
    cardHoverBtnColor: string;

    // 卡片配置
    cards: {
      title: string;
      desc: string;
      price: {
        value: number;
        unit: string;
      };

      actionText: string;
      actionBg: string;
      action: IActionProps;
    }[];
  };
}

interface IActionProps {
  actionType: number;
  href: string;
  /**选中商品 */
  selectGood: string;
  source_location?: string;
  skuId: string | number;
}

async function btnClickAction(props: IActionProps) {
  switch (props.actionType) {
    // 链接跳转
    case 0:
      window.open(props.href);
      break;
    // 购买弹框
    case 1:
      const { selectGood } = props;

      const {
        type,
        selectedPkg,
        selectedPlusSku,
        versionTab,
        selectSkuId,
        buyType,
      } = parseVipUrlHash("buyVip-" + selectGood);

      return BuyVipDialog.open({
        type,
        selectedPkg: selectedPkg.split(";"),
        defaultSelectPlusSku: selectedPlusSku,
        /**企业会员选中Version（基础授权、高级授权）使用 */
        skuId: props.skuId,
        versionTab,
        source: "导航",
        source_location: props.source_location,
        selectSkuId,
        buyType,
      });
    default:
      break;
  }
}

function Card({ item, conf }) {
  const ref = useRef();
  const isHovering = useHover(ref);

  const style = useMemo(() => {
    return isHovering
      ? {
          background: conf.popover.cardHoverBgColor,
          borderColor: conf.popover.cardHoverBorderColor,
        }
      : {};
  }, [isHovering, conf]);

  const btnStyle = useMemo(() => {
    return isHovering
      ? {
          background: item.actionBg || conf.popover.cardHoverBtnBg,
          color: item.actionColor || conf.popover.cardHoverBtnColor,
        }
      : {
          background: item.actionBg,
          color: item.actionColor,
        };
  }, [isHovering, conf, item]);

  const isNew = useIsNewHeader();

  return (
    <div
      ref={ref}
      className={classNames(styles.card, isNew && styles.newHeader)}
      style={style}
    >
      <div className={styles.title}>{item.title}</div>
      <div className={styles.desc}>{item.desc}</div>
      {item.price?.value && (
        <div className={styles.price}>
          ￥<span className={styles.num}>{item.price.value}</span>
          <span className={styles.unit}>{item.price.unit}</span>
        </div>
      )}

      <div
        className={styles.btn}
        style={btnStyle}
        onClick={() => {
          btnClickAction({
            ...item.action,
            source_location: `导航_${conf.text}_${item.actionText}`,
          });
        }}
        data-gd-click="button_click"
        data-button-name={item.actionText}
      >
        {item.actionText}
      </div>
    </div>
  );
}

function VipEntryItem(props: {
  conf: IConf;
  className?: string;
  isNew: boolean;
}) {
  const { conf, className, isNew } = props;
  const user = useAppStore((s) => s.user);

  const onClick = useMemoizedFn((e: React.MouseEvent) => {
    if (isNew) {
      e.preventDefault();
      if (!isGdMerged(user)) {
        goGdMerge({
          redirectUri: `${gaodingEditUrl}/pricing?from=gdmaterial`,
        });
      } else {
        window.open(`${gaodingEditUrl}/pricing?from=gdmaterial`);
      }
    }
  });

  return (
    <TrackerA
      className={classNames(className)}
      href={conf.href || "/vip/info"}
      data-content-source={`导航_${conf.text}`}
      data-gd-expose="resource_loc_expose"
      data-gd-click="resource_loc_click"
      data-resource-type="图片"
      data-resource-id="0"
      data-resource-title={`导航-${conf.text}`}
      target={isNew ? "_blank" : "_self"}
      onClick={onClick}
    >
      <Dropdown
        placement="bottomCenter"
        overlayClassName={styles.popover}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement.parentElement
        }
        mouseEnterDelay={0.3}
        mouseLeaveDelay={0.3}
        destroyPopupOnHide
        align={{
          offset: [-60, 10],
        }}
        overlay={
          <div
            data-content-source={`导航_${conf.text}`}
            data-gd-expose="resource_loc_expose"
            data-resource-type="下拉弹窗"
            data-resource-id="0"
            data-resource-title={`导航-${conf.text}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={styles.header}
              style={{
                backgroundImage: `url("${conf.popover.headBg[0].url}")`,
              }}
            >
              {conf.popover.title}

              {/* desc */}
              <div className={styles.desc}>
                {Array.isArray(conf.popover.desc) ? (
                  conf.popover.desc.map((item, index) => (
                    <span key={index}>{item.text}</span>
                  ))
                ) : (
                  <div>{conf.popover.desc}</div>
                )}
              </div>

              {conf.popover.moreHref && (
                <a
                  className={styles.more}
                  href={conf.popover.moreHref}
                  onClick={onClick}
                  target="_blank"
                  style={{
                    color: conf.popover.moreColor,
                  }}
                  data-gd-click="button_click"
                  data-button-name="了解更多"
                >
                  了解更多
                  <HBIcon type="ic_chevron_right" />
                </a>
              )}
            </div>

            <div className={styles.cards}>
              {conf.popover.cards.map((item, index) => (
                <Card item={item} conf={conf} key={index} />
              ))}
            </div>
          </div>
        }
      >
        {isNew ? (
          <Image
            className={styles.entryIcon}
            src={conf.icon[0]?.url}
            alt={conf.text}
            height={20}
            width={24}
          />
        ) : (
          <div
            className={styles.entryItem}
            style={{
              color: conf.color,
              background: conf.bg,
            }}
          >
            {conf.text}
            {conf.rightTag?.length > 0 && (
              <Image
                height={14}
                alt="tag"
                {...gdImgSrcPolicy(conf.rightTag[0]?.url, {
                  displayHeight: 14,
                })}
              />
            )}
          </div>
        )}
      </Dropdown>
    </TrackerA>
  );
}

export function VipRightNav() {
  const { data: vipConf } = useReq(async () => {
    return getGrocery<{
      vip: IConf[];
      mergedVip: IConf[];
      new: IConf[];
      newMerged: IConf[];
    }>("hb_nav_vip_entry");
  });

  const isNew = useIsNewHeader();

  let conf = isNew ? vipConf?.newMerged : vipConf?.mergedVip;

  if (!(conf?.length > 0)) {
    return null;
  }
  return conf.map((item, index) => (
    <VipEntryItem key={index} conf={item} isNew={isNew} />
  ));
}
